import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCompaniesByUserId } from "../../db/Companies";
import { generateStripeLinks } from "../../db/Subscriptions";
import { bundles } from "./BundlesData";
import { fetchUserSubscriptions } from "../../db/Subscriptions";
import { formatDateTime } from "../../globalFunctions/DateTimeDisplayCleaner";

const SubscriptionForRepresentative = ({ selectedUserId = null }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [companySelections, setCompanySelections] = useState({});
  const [stripeLinks, setStripeLinks] = useState({});
  const [subscribedCompanyIds, setSubscribedCompanyIds] = useState([]);
  const [existingSubscriptions, setExistingSubscriptions] = useState([]);

  useEffect(() => {
    const userIdToLoad = selectedUserId || currentUser?.id;

    if (userIdToLoad) {
      fetchCompaniesByUserId(userIdToLoad).then(setCompanies);
      fetchUserSubscriptions(userIdToLoad).then(setExistingSubscriptions);
    }
  }, [selectedUserId, currentUser]);

  useEffect(() => {
    const userIdToLoad = selectedUserId || currentUser?.id;
    if (userIdToLoad) {
      fetchCompaniesByUserId(userIdToLoad).then(setCompanies);
      fetchUserSubscriptions(userIdToLoad).then((subs) => {
        const ids = subs.map((s) => parseInt(s.companyId)).filter(Boolean);
        setSubscribedCompanyIds(ids);
      });
    }
  }, [selectedUserId, currentUser]);

  const updateSelection = (companyId, field, value) => {
    setCompanySelections((prev) => {
      const previous = prev[companyId] || {};
      let updated = { ...previous, [field]: value };

      // Si le champ modifié est le bundle, on initialise une durée et portée par défaut
      if (field === "bundleKey" && bundles[value]) {
        const durations = bundles[value].pricing.map((p) => p.duration);
        const uniqueDurations = [...new Set(durations)];
        const defaultDuration = uniqueDurations[0];

        const reaches = bundles[value].pricing
          .filter((p) => p.duration === defaultDuration)
          .map((p) => p.reach);
        const uniqueReaches = [...new Set(reaches)];
        const defaultReach = uniqueReaches[0];

        updated = {
          bundleKey: value,
          duration: defaultDuration,
          reach: defaultReach,
        };
      }

      return {
        ...prev,
        [companyId]: updated,
      };
    });
  };

  const handleGenerateStripeLinks = async () => {
    const subscriptions = Object.entries(companySelections)
      .map(([companyId, sel]) => {
        if (!sel.bundleKey || !sel.duration || !sel.reach) return null;

        return {
          companyId: parseInt(companyId),
          priceId: sel.bundleKey,
          duration: sel.duration,
          reach: sel.reach,
          userId: selectedUserId || currentUser?.id,
        };
      })

      .filter(Boolean);

    if (!subscriptions.length) return;

    try {
      const result = await generateStripeLinks({
        subscriptions,
        createdBy: currentUser.id,
      });

      const linksMap = {};
      result.forEach(({ companyId, url }) => {
        linksMap[companyId] = url;
      });

      setStripeLinks(linksMap);
    } catch (err) {
      console.error("Erreur génération liens Stripe", err);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">
        {t("becomePartner.pageTitle", "Devenir partenaire")}
      </h2>

      {companies.map((company) => {
        const sel = companySelections[company.id] || {};
        const selectedBundle = bundles[sel.bundleKey];
        const availableDurations = selectedBundle?.pricing
          .map((p) => p.duration)
          .filter((v, i, a) => a.indexOf(v) === i);
        const availableReaches = selectedBundle?.pricing
          .filter((p) => p.duration === sel.duration)
          .map((p) => p.reach)
          .filter((v, i, a) => a.indexOf(v) === i);

        const priceObj = selectedBundle?.pricing.find(
          (p) => p.duration === sel.duration && p.reach === sel.reach
        );

        const existing = existingSubscriptions.find(
          (sub) => parseInt(sub.companyId) === company.id
        );

        return (
          <div
            key={company.id}
            className="bg-gray-50 rounded-lg p-4 shadow-sm space-y-4 border border-papayaTitle"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {company.name}
            </h3>

            {existing ? (
              // ✅ Si déjà abonné
              <div className="text-sm text-green-700 font-medium mb-2 space-y-1">
                <div>
                  ✅{" "}
                  {existing.bundleLabel
                    ? existing.bundleLabel
                    : `${t(
                        `becomePartner.bundles.${existing.bundleKey}.name`,
                        existing.bundleKey
                      )} — ${existing.duration} ${t(
                        "global.months",
                        "mois"
                      )} — ${
                        existing.reach === "local"
                          ? t("reach.local", "Local")
                          : existing.reach === "regional"
                          ? t("reach.regional", "Régional")
                          : t("reach.provincial", "Provincial")
                      }`}
                </div>
                <div className="text-gray-600">
                  {t("subscription.startsAt", "Début")}:{" "}
                  {formatDateTime(existing.currentPeriodStart)}
                </div>
                <div className="text-gray-600">
                  {t("subscription.renewsAt", "Renouvellement")}:{" "}
                  {formatDateTime(existing.currentPeriodEnd)}
                </div>
              </div>
            ) : (
              // ❌ Si NON abonné → afficher tout le reste ici :
              <>
                {/* Forfait partenaire */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("becomePartner.bundle", "Forfait partenaire")}
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={sel.bundleKey || ""}
                    onChange={(e) =>
                      updateSelection(company.id, "bundleKey", e.target.value)
                    }
                  >
                    <option value="">{t("none", "Aucun")}</option>
                    {Object.entries(bundles).map(([key, bundle]) => (
                      <option key={key} value={key}>
                        {t(`becomePartner.bundles.${key}.name`, bundle.key)}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedBundle && availableDurations?.length > 0 && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("duration", "Durée")}
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {availableDurations.map((duration) => {
                        const isSelected = sel.duration === duration;
                        return (
                          <button
                            key={duration}
                            type="button"
                            onClick={() =>
                              updateSelection(company.id, "duration", duration)
                            }
                            className={`px-3 py-2 rounded-full border text-sm transition ${
                              isSelected
                                ? "bg-papayaTitle text-white border-papayaTitle"
                                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {duration} {t("global.months", "mois")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Portée */}
                {selectedBundle && sel.duration && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("reach", "Portée")}
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {availableReaches?.map((reach) => {
                        const isSelected = sel.reach === reach;
                        return (
                          <button
                            key={reach}
                            type="button"
                            onClick={() =>
                              updateSelection(company.id, "reach", reach)
                            }
                            className={`px-3 py-2 rounded-full border text-sm transition ${
                              isSelected
                                ? "bg-papayaTitle text-white border-papayaTitle"
                                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {reach === "local" &&
                              t(
                                "subscriptionModal.reachLocalDescription",
                                "Promotion locale"
                              )}
                            {reach === "regional" &&
                              t(
                                "subscriptionModal.reachRegionalDescription",
                                "Visibilité régionale"
                              )}
                            {reach === "provincial" &&
                              t(
                                "subscriptionModal.reachProvincialDescription",
                                "Portée provinciale"
                              )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Prix */}
                {priceObj && (
                  <div className="text-sm text-gray-800 bg-white mt-4 p-3 border border-gray-200 rounded-lg shadow-sm">
                    <p className="mb-1">
                      <span className="font-medium">
                        {t("becomePartner.fullPayment", "Paiement complet")}:
                      </span>{" "}
                      <span className="text-lg font-bold">
                        {priceObj.finalPrice.toFixed(2)} $
                      </span>
                    </p>
                    <p className="mb-1">
                      <span className="font-medium">
                        {t("perMonth", "Par mois")}:
                      </span>{" "}
                      <span className="italic">
                        {(priceObj.finalPrice / sel.duration).toFixed(2)} $
                      </span>
                    </p>

                    {/* Rabais */}
                    {(() => {
                      const original = priceObj.originalPrice;
                      const final = priceObj.finalPrice;
                      const savings =
                        original && final && original > final
                          ? original - final
                          : 0;
                      if (savings > 0) {
                        return (
                          <div className="mt-2 text-sm text-green-600 font-semibold">
                            {t("becomePartner.discount", "Rabais")}:{" "}
                            {savings.toFixed(2)} $ économisés!
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {/* Lien Stripe */}
                {stripeLinks[company.id] && (
                  <div className="pt-2">
                    <a
                      href={stripeLinks[company.id]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium bg-papayaTitle text-primary rounded-full p-3"
                    >
                      {t("subscriptionModal.payLink", "Payer l’abonnement")}
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Bouton générer */}
      <div className="text-right pt-6">
        <button
          onClick={handleGenerateStripeLinks}
          className="bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition"
        >
          {t(
            "subscriptionModal.confirmSubscription",
            "Générer les liens d'abonnement"
          )}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionForRepresentative;
